import { useTranslation } from 'react-i18next';
import { DeliverySourcePill } from '../../../LogisticsPage/panels/DeliverySource';
import { displayStatusName } from '../../../LogisticsPage/board.logic';
import { useLogisticsQueries } from '../../../LogisticsPage/useLogisticsQueries';
import { formatDay } from '../../../LogisticsPage/logistics.format';
import { quantityAfterJoin, type IJoinableOrder } from '../../stock/joinOrder';
import styles from './TaskPanelStockTab.module.css';

interface IStockOrderCardProps {
  siteId: string;
  /** The order already on its way, carrying the LINE the amount goes on. */
  order: IJoinableOrder;
  /** What the checkout adds to that line. */
  amount: number;
}

/**
 * An order already on its way, as the Stock tab checkout's review shows it:
 * the board card shrunk — its ref, the tasks it was raised from, the LINE for
 * this material, where it goes, its board column and dates — and what the
 * line comes to once the amount is added.
 */
export function StockOrderCard({ siteId, order, amount }: IStockOrderCardProps) {
  const { t, i18n } = useTranslation();
  // The board's own column set, shared with the Logistics page — the order's
  // status must read the same word here as it does on the board.
  const { statusesQuery } = useLogisticsQueries(siteId);
  const column = (statusesQuery.data ?? []).find((s) => s.id === order.status);
  const day = (isoDate: string) => formatDay(isoDate, i18n.language);
  const eta =
    order.dueDate === null
      ? t('scheduler.panel.stock.join.etaNotSet')
      : t('scheduler.panel.stock.join.eta', { date: day(order.dueDate) });
  // The " · " between facts is punctuation, not copy — the board card's meta
  // line and the delivery drawer's use the same literal.
  const meta = [
    order.destinationName,
    column ? displayStatusName(column, t) : order.status,
    ...(order.requestedDate === null
      ? []
      : [t('scheduler.panel.stock.join.requested', { date: day(order.requestedDate) })]),
    eta,
  ].join(' · ');

  return (
    <div className={styles.joinCard}>
      <div className={styles.joinTop}>
        <span className={styles.joinRef}>#{order.ref}</span>
        {/* The board card's own mark for a Scheduler-raised order — same
            component, same tooltip: the task names are a hover away rather
            than spelled out in a box this narrow. */}
        {order.line.sources.length > 0 ? <DeliverySourcePill sources={order.line.sources} /> : null}
        {/* The LINE, named and measured — never the truck's total: a card
            saying "9 t" about an order carrying three materials would be
            about someone else's concrete. */}
        <span className={styles.joinQty}>
          {order.line.quantity === null
            ? order.line.itemName
            : `${order.line.itemName} · ${order.line.quantity} ${order.line.unit}`}
        </span>
      </div>
      <span className={styles.joinMeta}>{meta}</span>
      <span className={styles.joinAfter}>
        {t('scheduler.panel.stock.join.after', { total: quantityAfterJoin(order.line, amount), unit: order.line.unit })}
      </span>
    </div>
  );
}
